import { ChevronRight, Fingerprint, Info, KeyRound, Trash2, UserRoundCog } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { buildLabel, PageBackLink, PageHeader, StateView } from "../components";
import { dateTime, useApiData } from "../hooks";
import { passkeyErrorMessage, passkeysAvailable, registerPasskey } from "../passkeys";

type PasskeyCredential = {
  id: string;
  label: string;
  deviceType: "singleDevice" | "multiDevice";
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
};

export function SettingsPage() {
  const { user } = useAuth();
  const canAdmin = user?.permissions.some((permission) => permission === "users.manage" || permission === "audit.read") ?? false;
  return <>
    <section className="page-intro"><div className="app-container"><PageHeader title="Configurações" /></div></section>
    <section className="app-section compact"><div className="app-container settings-hub">
      <Link className="workspace-card settings-option" to="/configuracoes/seguranca"><span className="settings-heading-icon"><Fingerprint size={20} /></span><span><strong>Biometria e passkeys</strong><small>Cadastre e gerencie formas de acesso deste aparelho.</small></span><ChevronRight size={18} /></Link>
      {canAdmin && <Link className="workspace-card settings-option" to="/administracao"><span className="settings-heading-icon is-admin"><UserRoundCog size={20} /></span><span><strong>Administração</strong><small>Usuários, permissões e auditoria.</small></span><ChevronRight size={18} /></Link>}
      <article className="workspace-card settings-about">
        <span className="settings-heading-icon is-about"><Info size={20} /></span>
        <span className="settings-about-heading"><strong>Sobre o sistema</strong><small>Autor: Lucas · Versão: {buildLabel}</small></span>
        <dl><div><dt>Autor</dt><dd>Lucas</dd></div><div><dt>Versão</dt><dd>{buildLabel}</dd></div></dl>
      </article>
    </div></section>
  </>;
}

export function SecuritySettingsPage() {
  const credentials = useApiData<PasskeyCredential[]>("/auth/passkeys");
  const [supported, setSupported] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => { void passkeysAvailable().then(setSupported); }, []);

  const activate = async () => {
    setBusy(true); setError("");
    try { await registerPasskey(); await credentials.reload(); }
    catch (caught) { setError(passkeyErrorMessage(caught, "Não foi possível ativar a biometria.")); }
    finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    setBusy(true); setError("");
    try { await api(`/auth/passkeys/${id}`, { method: "DELETE" }); setRemoving(null); await credentials.reload(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível remover a passkey."); }
    finally { setBusy(false); }
  };

  return <>
    <section className="page-intro"><div className="app-container"><PageBackLink to="/configuracoes" context="Biometria e passkeys" /></div></section>
    <section className="app-section compact"><div className="app-container security-settings-page">
      {error && <p className="form-error" role="alert">{error}</p>}
      <article className="workspace-card passkey-settings-card">
        <header><div><span className="passkey-settings-icon"><Fingerprint size={24} /></span><div><h3>Acessos cadastrados</h3><p>Use a segurança do aparelho para entrar sem digitar a senha.</p></div></div><button type="button" className="site-button site-button-red site-button-small" disabled={!supported || busy} onClick={() => void activate()}><Fingerprint size={17} />{busy ? "Ativando…" : "Adicionar passkey"}</button></header>
        {!supported && <p className="settings-inline-note">Este navegador ou aparelho não oferece uma autenticação local compatível.</p>}
        <StateView loading={credentials.loading} error={credentials.error} empty={(credentials.data?.length ?? 0) === 0} emptyTitle="Nenhuma passkey cadastrada" emptyMessage="Você continuará entrando com senha e MFA.">
          <div className="passkey-device-list">{(credentials.data ?? []).map((credential) => <div className="passkey-device" key={credential.id}>
            <span className="passkey-device-icon"><KeyRound size={18} /></span>
            <div><strong>{credential.label}</strong><small>{credential.lastUsedAt ? `Último uso ${dateTime(credential.lastUsedAt)}` : `Adicionada ${dateTime(credential.createdAt)}`}</small></div>
            {removing === credential.id ? <div className="passkey-remove-confirm"><button type="button" onClick={() => setRemoving(null)} disabled={busy}>Voltar</button><button type="button" className="danger" onClick={() => void remove(credential.id)} disabled={busy}>{busy ? "Removendo…" : "Confirmar"}</button></div> : <button type="button" className="icon-close" aria-label={`Remover ${credential.label}`} onClick={() => setRemoving(credential.id)}><Trash2 size={17} /></button>}
          </div>)}</div>
        </StateView>
      </article>
    </div></section>
  </>;
}
